/**
 * Space Actions - Canonical Mutation API for Windows
 * 
 * All window operations flow through these actions, which then use
 * the internal _applyLocalWindow* helpers.
 * 
 * All actions handle:
 * - CRDT synchronization
 * - Backend persistence
 * - State invariants (activeWindowId, z-index, etc.)
 */

import { NormalizedWindowEntity } from './spaceStore';
import { createFileLogger } from '../utils/logger';
import { generateWindowId } from '../components/Space/utils/windowUtils';

const log = createFileLogger('SPACE_ACTIONS');

export const createSpaceActions = (set: any, get: any) => ({
  /**
   * Create a new window
   * @param params - Window creation parameters
   * @returns Window ID
   */
  createWindow: async (params: {
    appType: string;
    entity?: any;
    title?: string;
    position?: { x: number; y: number };
    size?: { width: number; height: number };
    applicationState?: any;
    props?: Record<string, any>;
  }): Promise<string> => {
    // ✅ FIX: Always generate a proper window ID, never reuse entity llmId
    const windowId = generateWindowId();
    
    log.info(`[createWindow] Creating window: windowId=${windowId} appType=${params.appType}`);
    
    // Build the complete window object with all required fields
    const newWindow: NormalizedWindowEntity = {
      id: windowId,
      type: 'window',
      appType: params.appType,
      entityId: params.entity?._id || params.entity?.id,
      entityLlmId: params.entity?.llmId,
      entity: params.entity, // ✅ CRITICAL: Include entity reference
      position: params.position || { x: 100, y: 100 },
      size: params.size || { width: 800, height: 600 },
      applicationState: params.applicationState || {},
      props: params.props,
      title: params.title || params.entity?.name || `${params.appType.charAt(0).toUpperCase() + params.appType.slice(1)}`,
    };
    
    log.info(`[createWindow] 📝 Built window: windowId=${windowId} appType=${newWindow.appType} entityId=${newWindow.entityId || 'none'} title="${newWindow.title}"`);
    
    // Use the mutation funnel (upsert behavior)
    get()._applyLocalWindowUpdate(windowId, newWindow, {
      source: 'local',
      syncCRDT: true,
      log: true
    });
    
    log.info(`[createWindow] Window added to state: windowId=${windowId} total=${Object.keys(get().windows).length}`);

    // Emit entity.opened for all entity window creations so AgentSupervisor can react.
    // Centralised here so every open path (sidebar, search, chat cards, doc links, etc.)
    // fires the event — fire-and-forget, never fatal.
    if (params.entity?._id) {
      try {
        const { emitEvent, getActiveChatThread } = await import('../services/worldEventsService');
        const spaceId = get().activeSpace?.id || '';
        emitEvent('entity.opened', {
          entityId: params.entity._id,
          entityType: params.entity.entityType,
          name: params.entity.name || '',
          currentThreadId: getActiveChatThread(spaceId),
        }, spaceId);
      } catch { /* non-fatal */ }
    }

    return windowId;
  },

  /**
   * Update an existing window
   * @param windowId - Window ID to update
   * @param updates - Partial window updates
   */
  updateWindow: async (windowId: string, updates: Partial<NormalizedWindowEntity>): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow) {
      log.warn('[updateWindow] Window not found:', windowId);
      return;
    }
    
    log.info('[updateWindow] Updating window:', { windowId, updates });
    
    // ✅ NEW: Clean up NewWindow entity if navigating away from launcher
    const oldEntityId = currentWindow.entityId;
    const isNavigatingAway = updates.entityId !== undefined && updates.entityId !== oldEntityId;
    const isSwitchingApp = updates.appType !== undefined && updates.appType !== currentWindow.appType;
    
    console.log('[updateWindow] 🔍 Checking for NewWindow cleanup:', {
      oldEntityId: oldEntityId?.slice(-6),
      isNavigatingAway,
      isSwitchingApp,
      willCheckForCleanup: (isNavigatingAway || isSwitchingApp) && oldEntityId
    });
    
    if ((isNavigatingAway || isSwitchingApp) && oldEntityId) {
      const useEntityStore = (await import('./entityStore')).default;
      const oldEntity = useEntityStore.getState().entities.find(
        (e: any) => e._id === oldEntityId || e.id === oldEntityId
      );
      
      console.log('[updateWindow] Old entity check:', {
        found: !!oldEntity,
        entityType: oldEntity?.entityType,
        entityName: oldEntity?.name,
        isWindow: oldEntity?.entityType === 'Window'
      });
      
      // Delete temporary launcher entities (Window, OpenDialog)
      const isTransientEntity = oldEntity && (
        oldEntity.entityType === 'Window' ||
        oldEntity.entityType === 'OpenDialog'
      );
      
      if (isTransientEntity) {
        console.log('[updateWindow] 🗑️ 🗑️ 🗑️ DELETING TRANSIENT ENTITY:', oldEntityId.slice(-6), oldEntity.entityType, oldEntity.name);
        log.info('[updateWindow] 🗑️ Deleting temporary launcher entity:', oldEntityId, oldEntity.name);
        
        try {
          // ✅ CRITICAL: Use softDeleteEntity directly instead of entity_delete command
          // entity_delete closes ALL windows showing the entity, which breaks the update!
          // We just want to delete the entity without touching the window (we're updating it)
          const useEntityStore = (await import('./entityStore')).default;
          await useEntityStore.getState().actions.softDeleteEntity(oldEntityId);
          
          console.log('[updateWindow] ✅ ✅ ✅ TRANSIENT ENTITY DELETED:', oldEntity.entityType);
          log.info('[updateWindow] ✅ Temporary launcher entity deleted:', oldEntity.entityType);
        } catch (error) {
          console.error('[updateWindow] ❌ Failed to delete transient entity:', error);
          log.error('[updateWindow] Failed to delete transient entity:', error);
          // Continue anyway
        }
      } else {
        console.log('[updateWindow] Not a transient entity, keeping it');
      }
    }
    
    // Use helper to update window (handles CRDT sync and state update)
    get()._applyLocalWindowUpdate(windowId, updates, {
      source: 'local',
      syncCRDT: true,
      log: true
    });
  },

  /**
   * Close a window
   * @param windowId - Window ID to close
   */
  closeWindow: async (windowId: string): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow) {
      log.warn('[closeWindow] Window not found:', windowId);
      return;
    }
    
    log.info('[closeWindow] Closing window:', { windowId });
    
    // If window has a Window/OpenDialog entity backing it, soft delete that entity too
    // This includes:
    // 1. entityId - Direct Window/OpenDialog entity (NewWindow, OpenDialog)
    // 2. windowEntityId in applicationState - Tracking entity for regular apps
    if (currentWindow.entityId) {
      const useEntityStore = (await import('./entityStore')).default;
      const entity = useEntityStore.getState().entities.find(
        (e: any) => e._id === currentWindow.entityId
      );
      
      const isWindowEntity = entity && (entity.entityType === 'Window' || entity.entityType === 'OpenDialog');
      if (isWindowEntity) {
        log.info('[closeWindow] Deleting window entity:', {
          entityId: entity._id,
          entityType: entity.entityType,
          entityName: entity.name
        });
        
        try {
          // ✅ Use entityStore.actions for proper orchestration
          await useEntityStore.getState().actions.softDeleteEntity(entity._id);
          log.info('[closeWindow] ✅ Window entity deleted');
        } catch (error) {
          log.warn('[closeWindow] Failed to delete window entity (continuing with window close):', error);
        }
      }
    }
    
    // Also check for windowEntityId in applicationState (for app windows with tracking entities)
    const windowEntityId = currentWindow.applicationState?.windowEntityId;
    if (windowEntityId) {
      log.info('[closeWindow] Deleting tracking Window entity from applicationState:', {
        windowEntityId,
        appType: currentWindow.appType
      });
      
      try {
        const useEntityStore = (await import('./entityStore')).default;
        await useEntityStore.getState().actions.softDeleteEntity(windowEntityId);
        log.info('[closeWindow] ✅ Tracking Window entity deleted');
      } catch (error) {
        log.warn('[closeWindow] Failed to delete tracking Window entity:', error);
      }
    }
    
    // ✅ Use removeWindow instead of _applyLocalWindowDelete directly
    // This ensures focus management and child window cleanup work properly
    get().removeWindow(windowId);
  },

  /**
   * Set active window
   * @param windowId - Window ID to activate (null to clear)
   */
  setActiveWindow: (windowId: string | null): void => {
    log.info('[setActiveWindow] Setting active window:', windowId);
    
    set({ activeWindowId: windowId });
  },

  /**
   * Navigate window to a new entity (adds to navigation history)
   * @param windowId - Window ID to navigate
   * @param entityId - Entity ID to navigate to
   */
  navigateWindow: async (windowId: string, entityId: string): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow) {
      log.warn('[navigateWindow] Window not found:', windowId);
      return;
    }
    
    log.info(`[navigateWindow] Navigating window: windowId=${windowId} entityId=${entityId}`);
    
    // Clean up old transient (NewWindow/OpenDialog) entity if this window was showing one
    const oldEntityId = currentWindow.entityId;
    if (oldEntityId) {
      const useEntityStore = (await import('./entityStore')).default;
      const oldEntity = useEntityStore.getState().entities.find(
        (e: any) => e._id === oldEntityId || e.id === oldEntityId
      );
      const isTransientEntity = oldEntity && (
        oldEntity.entityType === 'Window' ||
        oldEntity.entityType === 'OpenDialog'
      );
      if (isTransientEntity) {
        log.info('[navigateWindow] Deleting transient launcher entity:', oldEntityId, oldEntity.entityType);
        try {
          await useEntityStore.getState().actions.softDeleteEntity(oldEntityId);
        } catch (error) {
          log.error('[navigateWindow] Failed to delete transient entity:', error);
        }
      }
    }
    
    // Get current history and index
    const history = currentWindow.navigationHistory || [];
    const currentIndex = currentWindow.navigationIndex ?? -1;
    
    // If we're not at the end of history, truncate forward history
    const newHistory = currentIndex >= 0 ? history.slice(0, currentIndex + 1) : [];
    
    // Add new entity to history
    newHistory.push(entityId);
    
    // Get the entity to navigate to
    const useEntityStore = (await import('./entityStore')).default;
    const entity = useEntityStore.getState().entities.find(
      (e: any) => e._id === entityId || e.id === entityId || e.llmId === entityId
    );
    
    if (!entity) {
      log.warn('[navigateWindow] Entity not found:', entityId);
      return;
    }
    
    const { getAppTypeForEntity } = await import('../utils/entityTypeMapping');
    const appType = getAppTypeForEntity(entity);
    
    // Update window with new entity and navigation state
    get()._applyLocalWindowUpdate(windowId, {
      entityId: entity._id || entity.id,
      entityLlmId: entity.llmId,
      appType,
      title: entity.name,
      navigationHistory: newHistory,
      navigationIndex: newHistory.length - 1,
      applicationState: {
        ...currentWindow.applicationState,
        isNew: false
      }
    }, {
      source: 'local',
      syncCRDT: true,
      log: true
    });

    log.info(`[navigateWindow] Navigation complete: windowId=${windowId} entityId=${entityId} historyLen=${newHistory.length}`);
  },

  /**
   * Clear the entityId (and optionally reset the title) from a window.
   * Called when an app deselects its current entity (e.g. navigating back to inbox).
   * Syncs to CRDT/backend so a refresh doesn't restore the stale reference.
   */
  clearWindowEntityId: (windowId: string, fallbackTitle?: string): void => {
    const currentWindow = get().windows[windowId];
    if (!currentWindow || !currentWindow.entityId) return;

    log.info(`[clearWindowEntityId] Clearing entity from window: windowId=${windowId} clearedId=${currentWindow.entityId} fallbackTitle="${fallbackTitle || ''}"`);
    const updates: Partial<import('./spaceStore').NormalizedWindowEntity> = {
      entityId: undefined,
      entityLlmId: undefined,
    };
    if (fallbackTitle !== undefined) {
      updates.title = fallbackTitle;
    }
    get()._applyLocalWindowUpdate(windowId, updates, {
      source: 'local',
      syncCRDT: true,
      log: false,
    });
  },

  /**
   * Navigate back in window history
   * @param windowId - Window ID to navigate
   */
  navigateBack: async (windowId: string): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow || !currentWindow.navigationHistory || currentWindow.navigationHistory.length === 0) {
      log.warn('[navigateBack] No history to navigate back');
      return;
    }
    
    const currentIndex = currentWindow.navigationIndex ?? currentWindow.navigationHistory.length - 1;
    
    if (currentIndex <= 0) {
      log.warn('[navigateBack] Already at beginning of history');
      return;
    }
    
    const newIndex = currentIndex - 1;
    const entityId = currentWindow.navigationHistory[newIndex];
    
    log.info('[navigateBack] Navigating back:', { windowId, fromIndex: currentIndex, toIndex: newIndex, entityId });
    
    // Get the entity
    const useEntityStore = (await import('./entityStore')).default;
    const entity = useEntityStore.getState().entities.find(
      (e: any) => e._id === entityId || e.id === entityId || e.llmId === entityId
    );
    
    if (!entity) {
      log.warn('[navigateBack] Entity not found in history:', entityId);
      return;
    }
    
    const { getAppTypeForEntity } = await import('../utils/entityTypeMapping');
    const appType = getAppTypeForEntity(entity);
    
    // Update window to show previous entity
    get()._applyLocalWindowUpdate(windowId, {
      entityId: entity._id || entity.id,
      entityLlmId: entity.llmId,
      appType,
      title: entity.name,
      navigationIndex: newIndex
    }, {
      source: 'local',
      syncCRDT: true,
      log: true
    });
  },

  /**
   * Navigate forward in window history
   * @param windowId - Window ID to navigate
   */
  navigateForward: async (windowId: string): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow || !currentWindow.navigationHistory || currentWindow.navigationHistory.length === 0) {
      log.warn('[navigateForward] No history to navigate forward');
      return;
    }
    
    const currentIndex = currentWindow.navigationIndex ?? currentWindow.navigationHistory.length - 1;
    
    if (currentIndex >= currentWindow.navigationHistory.length - 1) {
      log.warn('[navigateForward] Already at end of history');
      return;
    }
    
    const newIndex = currentIndex + 1;
    const entityId = currentWindow.navigationHistory[newIndex];
    
    log.info('[navigateForward] Navigating forward:', { windowId, fromIndex: currentIndex, toIndex: newIndex, entityId });
    
    // Get the entity
    const useEntityStore = (await import('./entityStore')).default;
    const entity = useEntityStore.getState().entities.find(
      (e: any) => e._id === entityId || e.id === entityId || e.llmId === entityId
    );
    
    if (!entity) {
      log.warn('[navigateForward] Entity not found in history:', entityId);
      return;
    }
    
    const { getAppTypeForEntity } = await import('../utils/entityTypeMapping');
    const appType = getAppTypeForEntity(entity);
    
    // Update window to show next entity
    get()._applyLocalWindowUpdate(windowId, {
      entityId: entity._id || entity.id,
      entityLlmId: entity.llmId,
      appType,
      title: entity.name,
      navigationIndex: newIndex
    }, {
      source: 'local',
      syncCRDT: true,
      log: true
    });
  },
});
